/* global React, Icon, Modal, Button, Segmented, fmt, fmt0 */
// ============================================================
// AI Rådgiver — lånevilkår for investeringsscenarioet
// ============================================================
// Vilkårene ligger i en modal fordi de bare gjelder når linjen finansieres med lån.
// Fire ekstra felter i selve scenarioskjemaet ville truffet alle, også de som betaler
// kontant. Modalen skriver rett inn i linjen — det finnes ingen egen «lagre», og
// kurven bak oppdaterer seg mens du drar i sliderne.
//
// Tallene i modalen kommer fra den samme nedbetalingsplanen som kurven bruker. Renten
// er brukerens eget anslag, ikke et tilbud, og det står i modalen.
function AiiLoanSlider({ label, value, min, max, step, suffix, onChange }) {
  return <div className="aii-scn-slider">
    <div className="aii-scn-slider-head">
      <span>{label}</span>
      <b>{value}{suffix}</b>
    </div>
    <input className="aii-scn-range" type="range" min={min} max={max} step={step} value={value}
      onChange={(event) => onChange(Number(event.target.value))} />
  </div>;
}

// Nedbetalingsplanen vises rad for rad. Den kan bli lang, så tabellen scroller i seg
// selv i stedet for å skyve resten av modalen ut av skjermen.
function AiiLoanPlan({ rows }) {
  if (!rows?.length) return null;
  return <div className="aii-scn-plan">
    <div className="aii-scn-plan-row aii-scn-plan-head">
      <span>{t('adv.scn.plan.month')}</span>
      <span data-help={t('helpTopics.instalment.body')} data-help-title={t('helpTopics.instalment.title')}>{t('adv.scn.plan.payment')}</span>
      <span data-help={t('helpTopics.interest.body')} data-help-title={t('helpTopics.interest.title')}>{t('adv.scn.plan.interest')}</span>
      <span data-help={t('helpTopics.principal.body')} data-help-title={t('helpTopics.principal.title')}>{t('adv.scn.plan.principal')}</span>
      <span>{t('adv.scn.plan.remaining')}</span>
    </div>
    <div className="aii-scn-plan-body">
      {rows.map((row) => <div key={row.index} className="aii-scn-plan-row">
        <span>{row.index + 1}</span>
        <span>{fmt(row.payment)}</span>
        <span>{fmt(row.interest)}</span>
        <span>{fmt(row.principal)}</span>
        <span>{fmt(row.remaining)}</span>
      </div>)}
    </div>
  </div>;
}

function AiiLoanModal({ open, row, rowNumber, onChange, onClose }) {
  const [showPlan, setShowPlan] = React.useState(false);
  React.useEffect(() => { if (!open) setShowPlan(false); }, [open]);
  if (!open || !row) return null;
  const loan = globalThis.PlummeAiAdvisorLoan;
  const amount = Math.max(0, Number(row.amount) || 0);
  const equityPct = Math.min(100, Math.max(0, Number(row.equityPct) || 0));
  const equity = Math.round(amount * equityPct / 100);
  const principal = Math.max(0, amount - equity);
  const schedule = loan ? loan.buildSchedule({
    principal,
    annualRatePct: Number(row.interestPct) || 0,
    termMonths: Number(row.termMonths) || 1,
    type: row.loanType,
  }) : [];
  const totals = loan ? loan.summarize(schedule) : { totalInterest: 0, totalPaid: 0, firstPayment: 0, lastPayment: 0 };
  return <Modal open={open} onClose={onClose} title={t('adv.scn.loan.title', { n: rowNumber })} max={640} maxHeight="88vh" portal
    footer={<Button variant="primary" icon="check" onClick={onClose} data-help={t('helpTopics.scenario.body')} data-help-title={t('helpTopics.scenario.title')}>{t('adv.scn.loan.done')}</Button>}>
    <div className="aii-scn-loan">
      <div className="aii-scn-loan-type">
        <span className="aii-scn-loan-lbl" data-help={t('helpTopics.repaymentType.body')} data-help-title={t('helpTopics.repaymentType.title')}>{t('adv.scn.field.loanType')}</span>
        <Segmented value={row.loanType || 'annuity'} onChange={(value) => onChange('loanType', value)}
          options={['annuity', 'serial'].map((option) => ({ value: option, label: t('adv.scn.option.' + option) }))} />
        <p className="t-xs muted">{t('adv.scn.loan.typeHelp.' + (row.loanType === 'serial' ? 'serial' : 'annuity'))}</p>
      </div>
      <label className="aii-scn-loan-field">
        {t('adv.scn.field.interestPct')}
        <Input className="input" type="number" step="0.1" min="0" max="25" value={row.interestPct ?? 0}
          onChange={(event) => onChange('interestPct', Number(event.target.value) || 0)} />
      </label>
      <AiiLoanSlider label={t('adv.scn.field.equityPct')} value={equityPct} min={0} max={100} step={1} suffix=" %"
        onChange={(value) => onChange('equityPct', value)} />
      <AiiLoanSlider label={t('adv.scn.field.termMonths')} value={Math.max(1, Number(row.termMonths) || 1)} min={6} max={120} step={1}
        suffix={' ' + t('adv.scn.loan.monthsUnit')} onChange={(value) => onChange('termMonths', value)} />
      <div className="aii-scn-loan-keys">
        <div><span>{t('adv.scn.loan.equityNow')}</span><b>{fmt(equity)}</b></div>
        <div><span data-help={t('helpTopics.principal.body')} data-help-title={t('helpTopics.principal.title')}>{t('adv.scn.loan.principal')}</span><b>{fmt(principal)}</b></div>
        <div><span>{t('adv.scn.loan.firstPayment')}</span><b>{fmt(totals.firstPayment)}</b></div>
        <div><span>{t('adv.scn.loan.lastPayment')}</span><b>{fmt(totals.lastPayment)}</b></div>
        <div><span>{t('adv.scn.loan.totalInterest')}</span><b>{fmt(totals.totalInterest)}</b></div>
        <div><span>{t('adv.scn.loan.totalPaid')}</span><b>{fmt(totals.totalPaid)}</b></div>
      </div>
      <button type="button" className="aii-scn-plan-toggle" onClick={() => setShowPlan((current) => !current)}>
        <Icon name={showPlan ? 'chevUp' : 'chevDown'} size={14} />
        {showPlan ? t('adv.scn.loan.hidePlan') : t('adv.scn.loan.showPlan')}
      </button>
      {showPlan && <AiiLoanPlan rows={schedule} />}
      <p className="t-xs muted">{t('adv.scn.loan.note')}</p>
    </div>
  </Modal>;
}

Object.assign(window, { AiiLoanModal });
